import { useEffect, useState, type FormEvent } from 'react';
import type { Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useProjectMutations } from '@/api/hooks';
import { useCan } from '@/lib/permissions';
import { showErrorToast } from '@/lib/toasts';
import { generalFailure } from './generalFailure';
import { fieldError, followsSaved, generalDirty, generalErrors, generalPatch, hasGeneralErrors, savedValues, type GeneralErrors, type GeneralValues } from './generalModel';

/** The two inputs, kept in step with the saved project unless they have unsaved edits. */
function useGeneralDraft(project: Project) {
  const [values, setValues] = useState<GeneralValues>(savedValues(project));
  const [base, setBase] = useState<GeneralValues>(savedValues(project));
  useEffect(() => {
    if (followsSaved(values, base)) setValues(savedValues(project));
    setBase(savedValues(project));
  }, [project.name, project.description]); // only when the saved project changes
  return { values, setName: (name: string) => setValues((v) => ({ ...v, name })), setDescription: (description: string) => setValues((v) => ({ ...v, description })) };
}

/** State and submit of the General card (`PATCH /projects/{id}` with If-Match). */
export function useGeneralForm(project: Project) {
  const { update } = useProjectMutations();
  const can = useCan('projects:write', project.id);
  const { values, setName, setDescription } = useGeneralDraft(project);
  const [submitted, setSubmitted] = useState(false);
  const [server, setServer] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<unknown>(null);
  const local = generalErrors(values);
  const dirty = generalDirty(values, project);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setServer({});
    setProblem(null);
    if (hasGeneralErrors(local) || !dirty) return;
    try {
      const saved = await update.mutateAsync({ projectId: project.id, body: generalPatch(values, project), etag: project.etag });
      toast.success(`${saved.name} saved`);
      setSubmitted(false);
    } catch (err) {
      const failure = generalFailure(err);
      if (failure.server) setServer(failure.server);
      if ('problem' in failure) setProblem(failure.problem);
      if (failure.toast) showErrorToast(failure.toast);
    }
  };
  const errorFor = (f: keyof GeneralErrors) => fieldError(server, local, submitted, f);
  return { ...values, setName, setDescription, can, dirty, problem, errorFor, onSubmit, isPending: update.isPending };
}
